<template>
  <div id="conteudo-acao" v-if="!carregando && dadosAcaoResponse" v-show="!telaAberta">
    <Breadcrumb :items="breadcrumbItems" />

    <div id="titulo">
      <h2>{{ dadosAcao?.titulo }}</h2>
      <h4>Coordenador: {{ formatarEnum(dadosAcao?.nomeCoordenador) }}</h4>
    </div>


    <section id="menu-container">
      <!-- Menu mobile -->
      <div class="menu-mobile">
        <button class="menu-mobile-toggle" type="button" :aria-expanded="menuMobileAberto"
          aria-controls="opcoes-menu-mobile" @click="menuMobileAberto = !menuMobileAberto">
          <span>{{ nomeMenuAtual }}</span>

          <svg :class="{ 'seta-aberta': menuMobileAberto }" width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <div v-if="menuMobileAberto" id="opcoes-menu-mobile" class="menu-mobile-opcoes">
          <router-link v-for="item in itensMenu" :key="item.label" :to="item.to" :class="{ selecionado: item.ativo }"
            @click="menuMobileAberto = false">
            {{ item.label }}
          </router-link>
        </div>
      </div>

      <!-- Menu desktop -->
      <div id="menus">
        <div v-for="item in itensMenu" :key="item.label" class="item" :class="{ 'opcao-selecionada': item.ativo }">
          <router-link :to="item.to">
            {{ item.label }}
          </router-link>
        </div>
      </div>
    </section>


    <!-- BOTÕES DE CADASTRO (condicionais) - MANTENDO A MESMA ESTRUTURA -->

    <!-- PARTE DINÂMICA - ROTEAMENTO -->
    <div id="conteudo-dinamico">
      <router-view v-slot="{ Component }">
        <component :is="Component" :key="$route.path" :class="{
          cadastro:
            !$route.path.includes('central-coordenacao') &&
            !$route.path.includes('informacoes') &&
            !$route.path.includes('parcerias') &&
            !$route.path.includes('dashboard'),
        }" :acaoId="acaoId" :isCoordenador="isCoordenadorDaAcao" @atividadeSelecionada="abrirTela('atividade', $event)"
          @noticiaSelecionada="abrirTela('noticia', $event)" @abrir-tarefa="abrirTela('tarefa', $event)" />
      </router-view>
    </div>
  </div>

  <carregamento v-if="carregando" />
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useRoute } from "vue-router";
import acaoService from "@/service/acaoService";
import userInfos from "@/utils/userInfos";
import { formatarEnum } from "@/utils/FormatarEntradas";
import Carregamento from "@/components/common/Carregamento.vue";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import { useAuthStore } from "@/stores/auth";



const route = useRoute();
const authStore = useAuthStore();
const isLogado = computed(() => authStore.isAutenticado);
const isAdmin = computed(
  () => authStore.hasRole("ADMINISTRADOR") || authStore.hasRole("ADMIN")
);
const isPrex = computed(() => authStore.hasRole("PREX"));
const podeVerPainel = computed(
  () =>
    isLogado.value &&
    (isAdmin.value || isPrex.value || isCoordenadorDaAcao.value)
);

const carregando = ref(false);
const dadosAcaoResponse = ref({});
const acaoId = ref(route.params.id);
const isCoordenador = ref(false);
const isCoordenadorDaAcao = ref(false);

const telaAberta = ref(false);
const atividadeSelecionada = ref(false);
const noticiaSelecionada = ref(false);
const tarefaSelecionada = ref(false);
const atividadeAberta = ref(null);
const noticiaAberta = ref(null);
const tarefaAberta = ref(null);

const breadcrumbItems = computed(() => {
  return [
    { label: "Início", to: "/" },
    { label: "Ações", to: "/suas-acoes" },
    { tipo: "acao", id: acaoId.value, to: `/acao/${acaoId.value}` },
    {
      label: nomeDaRotaAtual.value,
      to: `/acao/${acaoId.value}/${rotaAtual.value}`,
    },
  ];
});

const dadosAcao = computed(() => {
  return (
    dadosAcaoResponse.value || {
      titulo: "Título Padrão",
      nomeCoordenador: "Coordenador Padrão",
      tipoAcao: "Projeto",
    }
  );
});

const mapeamentoRotas = {
  "central-coordenacao": "Central da Ação",
  "atividades-acao": "Atividades",
  "noticias-acao": "Notícias",
  "vagas-acao": "Vagas",
  "parcerias-acao": "Parcerias",
  atividades: "Atividades",
  noticias: "Notícias",
  vagas: "Vagas",
  informacoes: "Informações",
  declaracoes: "Minhas Declarações",
  tarefas: "Tarefas",
  dashboard: "Painel da Ação",
};

const rotaAtual = computed(() => {
  const path = route.path;
  for (const rota of Object.keys(mapeamentoRotas)) {
    if (path.includes(rota)) {
      return rota;
    }
  }
  return "";
});

const nomeDaRotaAtual = computed(() => {
  return mapeamentoRotas[rotaAtual.value] || "Página Atual";
});

const podeNavegarParaRotaAtual = computed(() => {
  const rotasSemLink = [
    "cadastrar-atividade",
    "cadastrar-noticia",
    "cadastrar-tarefa",
  ];
  return !rotasSemLink.some((rota) => route.path.includes(rota));
});

const menuMobileAberto = ref(false);

const itensMenu = computed(() => [
  {
    label: "Sobre a ação",
    to: `/acao/${acaoId.value}/informacoes`,
    ativo: route.path.includes("informacoes"),
    mostrar: true,
  },
  {
    label: "Atividades",
    to: `/acao/${acaoId.value}/atividades-acao`,
    ativo:
      route.path.includes("atividades-acao") ||
      route.name === "acao-atividades" ||
      (route.path.includes("atividades") &&
        !route.path.includes("cadastrar-atividade") &&
        !route.path.includes("central-coordenacao")),
    mostrar: true,
  },
  {
    label: "Notícias",
    to: `/acao/${acaoId.value}/noticias-acao`,
    ativo:
      route.path.includes("noticias-acao") ||
      route.name === "acao-noticias" ||
      (route.path.includes("noticias") &&
        !route.path.includes("cadastrar-noticia") &&
        !route.path.includes("central-coordenacao")),
    mostrar: true,
  },
  {
    label: "Vagas",
    to: `/acao/${acaoId.value}/vagas-acao`,
    ativo:
      route.path.includes("vagas-acao") ||
      route.name === "acao-vagas" ||
      route.path.includes("vagas"),
    mostrar: true,
  },
  {
    label: "Painel da Ação",
    to: `/acao/${acaoId.value}/dashboard`,
    ativo: route.path.includes("dashboard"),
    mostrar: true,
  },
  {
    label: "Central da Ação",
    to: `/acao/${acaoId.value}/central-coordenacao`,
    ativo: route.path.includes("central-coordenacao"),
    mostrar: isCoordenadorDaAcao.value,
  },
].filter((item) => item.mostrar));

const nomeMenuAtual = computed(
  () => itensMenu.value.find((item) => item.ativo)?.label || "Navegar pela ação"
);

async function buscarDadosAcao() {
  if (!acaoId.value || acaoId.value === "undefined") {
    carregando.value = false;
    return;
  }
  isCoordenadorDaAcao.value = false;
  carregando.value = true;
  try {
    const response = await acaoService.getAcoesById(acaoId.value);
    if (response) {
      dadosAcaoResponse.value = response;
      if (dadosAcaoResponse.value.titulo) {
        dadosAcaoResponse.value.titulo = dadosAcaoResponse.value.titulo.replace(
          /"/g,
          ""
        );
      }
      acaoId.value = response.id;

      // Verificar se é coordenador desta ação específica
      if (isLogado.value) {
        try {
          const ehCoord = await acaoService.verificarCoordenador(acaoId.value);
          isCoordenadorDaAcao.value = !!ehCoord;
        } catch (e) {
          isCoordenadorDaAcao.value = false;
        }
      } else {
        isCoordenadorDaAcao.value = false;
      }
    }
  } catch (error) {
    console.error("Erro ao buscar dados da ação:", error);
  } finally {
    carregando.value = false;
  }
}

function checkCoordenador() {
  if (!isLogado.value) {
    isCoordenador.value = false;
    return;
  }
  const user = userInfos.getUserInfos();
  const userScope = user?.scope || "";
  isCoordenador.value = userScope.split(" ").includes("SCOPE_COORDENADOR");
}

function abrirTela(tela, dados) {
  telaAberta.value = true;

  if (tela === "atividade") {
    atividadeSelecionada.value = true;
    atividadeAberta.value = dados;
  } else if (tela === "noticia") {
    noticiaSelecionada.value = true;
    noticiaAberta.value = dados;
  } else if (tela === "tarefa") {
    tarefaSelecionada.value = true;
    tarefaAberta.value = dados;
  }
}

function fecharTela(tela) {
  telaAberta.value = false;

  if (tela === "atividade") {
    atividadeSelecionada.value = false;
    atividadeAberta.value = null;
  } else if (tela === "noticia") {
    noticiaSelecionada.value = false;
    noticiaAberta.value = null;
  } else if (tela === "tarefa") {
    tarefaSelecionada.value = false;
    tarefaAberta.value = null;
  }
}

function analiseTipoAcao() {
  return dadosAcao.value.tipoAcao === "Curso" ||
    dadosAcao.value.tipoAcao === "Evento"
    ? "Meus Certificados"
    : "Minhas Declarações";
}

watch(
  () => route.params.id,
  async (novoId) => {
    if (novoId) {
      acaoId.value = novoId;
      isCoordenadorDaAcao.value = false;
      acaoService.limparCacheCoordenador(novoId);
      await buscarDadosAcao();
    }
  },
  { immediate: true }
);

onMounted(() => {
  checkCoordenador();
});
</script>

<style scoped>
#conteudo-acao {
  display: block;
  background-color: transparent;
}

#titulo {
  padding-bottom: 20px;
  border-radius: 10px 10px 0 0;
}

#titulo h2 {
  font-size: 1.8rem;
  color: var(--cinza-escuro);
  margin-bottom: 0.5rem;
}

#titulo h4 {
  font-size: 1rem;
  color: var(--cinza-medio);
  margin: 0;
}

#menus {
  display: flex;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap;
  width: 100%;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid var(--roxo-primario) !important;
}

.item {
  display: flex;
  flex: 1;
  min-width: 160px;
}

#menus .item a {
  width: 100%;
  border: none;
  padding: 12px 15px;
  font-size: 1rem;
  cursor: pointer;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
  color: var(--branco);
  background-color: var(--roxo-primario);
  text-decoration: none;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}

.ultimo-item a {
  border-right: none !important;
}

.opcao-selecionada a {
  background-color: var(--branco) !important;
  color: var(--preto) !important;
}

#menus .item a:hover {
  background-color: var(--roxo-medio);
  color: var(--branco);
}

/* Seletor mobile oculto no desktop */
.menu-mobile {
  display: none;
}

@media (max-width: 576px) {
  .menu-mobile {
    display: block;
    position: relative;
    width: 100%;
    margin-bottom: 0px;
    z-index: 10;
  }

  .menu-mobile-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-height: 48px;
    padding: 12px 16px;

    border: 1px solid var(--roxo-primario);
    border-radius: 8px;
    background: var(--roxo-primario);
    color: var(--branco);

    font: inherit;
    font-size: 0.95rem;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }

  .menu-mobile-toggle span {
    overflow-wrap: anywhere;
  }

  .menu-mobile-toggle svg {
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  .menu-mobile-toggle svg.seta-aberta {
    transform: rotate(180deg);
  }

  .menu-mobile-opcoes {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;

    display: flex;
    flex-direction: column;
    max-height: 60vh;
    overflow-y: auto;

    background: var(--branco);
    border: 1px solid var(--roxo-primario);
    border-radius: 8px;
    box-shadow: 0 8px 24px rgb(0 0 0 / 15%);
  }

  .menu-mobile-opcoes a {
    padding: 13px 16px;
    color: var(--cinza-escuro);
    text-decoration: none;
    border-bottom: 1px solid var(--cinza-claro);
    overflow-wrap: anywhere;
  }

  .menu-mobile-opcoes a:last-child {
    border-bottom: none;
  }

  .menu-mobile-opcoes a.selecionado {
    background: var(--roxo-primario);
    color: var(--branco);
    font-weight: 600;
  }

  #menus {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .menu-mobile-toggle svg {
    transition: none;
  }
}

@media (max-width: 768px) {


  #menus .item a {
    padding: 10px 8px;
    font-size: 0.9rem;
  }

  #titulo h2 {
    font-size: 1.6rem;
  }

  #titulo h4 {
    font-size: 0.9rem;
  }

  #conteudo-acao {
    padding: 0px;
  }
}
</style>
